/**
 * CharacterNodeBottomPanel -- Tabbed floating panel for character node actions.
 *
 * Contains 4 tabs:
 * - Refine: Prompt input, style selector, iteration count, generate button
 * - Voice: Voice selector placeholder with current voice display
 * - Variants: Image history grid with selection
 * - References: Reference image picker with add/remove
 *
 * Follows floating panel pattern: nopan nodrag, inverse-zoom scaling,
 * spring animation (stiffness 400, damping 25).
 */

import { memo, useState, useEffect, useMemo } from 'react'
import { useViewport } from 'reactflow'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '../../../../lib/utils'
import {
  ImagePickerModal,
  type SelectedImage,
} from '../../../chat/ImagePickerModal'
import { getThumbnailUrl } from '../../../../api/useAssetUrl'
import type { HistoryEntry } from './types'

export type BottomPanelTab = 'refine' | 'voice' | 'variants' | 'references'

interface StyleOption {
  id: string
  label: string
}

interface CharacterNodeBottomPanelProps {
  isVisible: boolean
  activeTab: BottomPanelTab
  onTabChange: (tab: BottomPanelTab) => void
  onClose: () => void
  isRunning: boolean
  projectId?: string
  // Refine tab props
  feedbackText: string
  onFeedbackTextChange: (text: string) => void
  onSubmit: () => void
  placeholder?: string
  styles?: StyleOption[]
  selectedStyle?: string
  onStyleChange?: (styleId: string) => void
  // Voice tab props
  voiceName?: string
  voiceSampleUrl?: string | null
  isVoicePlaying?: boolean
  onPlayVoice?: (e: React.MouseEvent) => void
  onSelectVoice?: () => void
  // Variants tab props
  history?: HistoryEntry[]
  pickedIndex?: number
  onSelectVariant?: (index: number) => void
  batchPending?: number
  cacheKey?: string
  // References tab props
  referenceImages?: string[]
  onAddImages?: (images: SelectedImage[]) => void
  onRemoveImage?: (index: number) => void
}

const TABS: { id: BottomPanelTab; label: string }[] = [
  { id: 'refine', label: 'Refine' },
  { id: 'voice', label: 'Voice' },
  { id: 'variants', label: 'Variants' },
  { id: 'references', label: 'References' },
]

export const CharacterNodeBottomPanel = memo(
  function CharacterNodeBottomPanel({
    isVisible,
    activeTab,
    onTabChange,
    onClose: _onClose,
    isRunning,
    projectId,
    // Refine
    feedbackText,
    onFeedbackTextChange,
    onSubmit,
    placeholder = "Describe the changes you'd like to make...",
    styles,
    selectedStyle,
    onStyleChange,
    // Voice
    voiceName,
    voiceSampleUrl,
    isVoicePlaying = false,
    onPlayVoice,
    // Variants
    history = [],
    pickedIndex = 0,
    onSelectVariant,
    batchPending = 0,
    cacheKey,
    // References
    referenceImages = [],
    onAddImages,
    onRemoveImage,
  }: CharacterNodeBottomPanelProps) {
    const { zoom } = useViewport()
    const [showStyleMenu, setShowStyleMenu] = useState(false)
    const [iterations, setIterations] = useState(1)
    const [showIterationMenu, setShowIterationMenu] = useState(false)
    const [showImagePicker, setShowImagePicker] = useState(false)

    // Reset dropdown states when panel closes
    useEffect(() => {
      if (!isVisible) {
        setShowStyleMenu(false)
        setShowIterationMenu(false)
        setShowImagePicker(false)
      }
    }, [isVisible])

    const selectedStyleLabel =
      styles?.find((s) => s.id === selectedStyle)?.label || 'Style'
    const scale = Math.min(Math.max(1 / zoom, 0.5), 2)

    // Variants display
    const variantCount = history.length + batchPending

    // Reference thumbnails
    const referenceThumbnails = useMemo(() => {
      if (!projectId) return []
      return referenceImages
        .map((path, i) => ({
          path,
          url: getThumbnailUrl(projectId, path, 60, cacheKey),
          index: i,
        }))
        .filter(
          (r): r is { path: string; url: string; index: number } =>
            r.url !== null
        )
    }, [projectId, referenceImages, cacheKey])

    const handleImagesConfirm = (images: SelectedImage[]) => {
      onAddImages?.(images)
      setShowImagePicker(false)
    }

    return (
      <>
        {projectId && (
          <ImagePickerModal
            isOpen={showImagePicker}
            onClose={() => setShowImagePicker(false)}
            projectId={projectId}
            onConfirm={handleImagesConfirm}
          />
        )}

        <AnimatePresence>
          {isVisible && (
            <motion.div
              initial={{ opacity: 0, y: -10, scale, x: '-50%' }}
              animate={{ opacity: 1, y: 0, scale, x: '-50%' }}
              exit={{ opacity: 0, y: -10, scale, x: '-50%' }}
              transition={{
                type: 'spring',
                stiffness: 400,
                damping: 25,
                opacity: { duration: 0.15 },
              }}
              className="absolute z-50 nopan nodrag"
              style={{
                top: '100%',
                left: '50%',
                marginTop: 8,
                transformOrigin: 'top center',
                width: 'calc(100% + 120px)',
              }}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-surface-1 border border-border rounded-xl shadow-2xl overflow-hidden">
                {/* Tab Bar */}
                <div className="flex gap-1 p-2 border-b border-border-subtle">
                  {TABS.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => onTabChange(tab.id)}
                      className={cn(
                        'flex-1 px-3 py-1.5 text-xs font-medium rounded-full relative transition-all duration-200',
                        activeTab === tab.id
                          ? ''
                          : 'text-text-muted hover:text-foreground'
                      )}
                      style={
                        activeTab === tab.id
                          ? {
                              backgroundColor: 'rgba(255, 255, 255, 0.1)',
                              color: '#ffffff',
                            }
                          : undefined
                      }
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                {/* Tab Content -- 220px fixed height */}
                <div className="h-[220px]">
                  {/* Refine Tab */}
                  {activeTab === 'refine' && (
                    <div className="h-full flex flex-col">
                      <div className="p-3 flex-1">
                        <textarea
                          value={feedbackText}
                          onChange={(e) =>
                            onFeedbackTextChange(e.target.value)
                          }
                          placeholder={placeholder}
                          maxLength={1000}
                          rows={2}
                          className={cn(
                            'w-full text-sm text-foreground bg-transparent',
                            'border-0 px-1 py-1',
                            'resize-none focus:outline-none',
                            'placeholder:text-text-muted nowheel',
                            'leading-relaxed'
                          )}
                          onKeyDown={(e) => {
                            if (
                              e.key === 'Enter' &&
                              (e.metaKey || e.ctrlKey) &&
                              feedbackText.trim()
                            ) {
                              e.preventDefault()
                              onSubmit()
                            }
                          }}
                        />
                      </div>
                      <div className="px-3 py-2 border-t border-border-subtle flex items-center justify-between">
                        <div className="flex items-center gap-1">
                          {styles &&
                            styles.length > 0 &&
                            onStyleChange && (
                              <div className="relative">
                                <button
                                  onClick={() =>
                                    setShowStyleMenu(!showStyleMenu)
                                  }
                                  className="h-7 px-2.5 flex items-center gap-1.5 rounded-full text-xs text-text-secondary hover:text-foreground hover:bg-surface-2 transition-all"
                                >
                                  <svg
                                    className="w-3.5 h-3.5"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={2}
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M9.53 16.122a3 3 0 00-5.78 1.128 2.25 2.25 0 01-2.4 2.245 4.5 4.5 0 008.4-2.245c0-.399-.078-.78-.22-1.128zm0 0a15.998 15.998 0 003.388-1.62m-5.043-.025a15.994 15.994 0 011.622-3.39m3.421 3.415a15.995 15.995 0 004.764-4.648l3.876-5.814a1.151 1.151 0 00-1.597-1.597L14.146 6.32a15.996 15.996 0 00-4.649 4.764m3.42 3.42a6.776 6.776 0 00-3.42-3.42"
                                    />
                                  </svg>
                                  <span>{selectedStyleLabel}</span>
                                  <svg
                                    className="w-2.5 h-2.5 text-text-muted"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={2}
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M19 9l-7 7-7-7"
                                    />
                                  </svg>
                                </button>
                                <AnimatePresence>
                                  {showStyleMenu && (
                                    <motion.div
                                      initial={{ opacity: 0, y: 4 }}
                                      animate={{ opacity: 1, y: 0 }}
                                      exit={{ opacity: 0, y: 4 }}
                                      className="absolute bottom-full left-0 mb-1 bg-surface-1 border border-border rounded-lg shadow-xl overflow-hidden min-w-[120px] z-10"
                                    >
                                      {styles.map((style) => (
                                        <button
                                          key={style.id}
                                          onClick={() => {
                                            onStyleChange(style.id)
                                            setShowStyleMenu(false)
                                          }}
                                          className={cn(
                                            'w-full px-3 py-2 text-xs text-left transition-colors',
                                            selectedStyle === style.id
                                              ? 'bg-surface-2 text-foreground'
                                              : 'text-text-secondary hover:bg-surface-2 hover:text-foreground'
                                          )}
                                        >
                                          {style.label}
                                        </button>
                                      ))}
                                    </motion.div>
                                  )}
                                </AnimatePresence>
                              </div>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="relative">
                            <button
                              onClick={() =>
                                setShowIterationMenu(!showIterationMenu)
                              }
                              className="h-7 px-2.5 flex items-center gap-1 rounded-full text-xs text-text-secondary hover:text-foreground hover:bg-surface-2 font-medium transition-all"
                            >
                              {iterations}x
                              <svg
                                className="w-2.5 h-2.5 text-text-muted"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M19 9l-7 7-7-7"
                                />
                              </svg>
                            </button>
                            <AnimatePresence>
                              {showIterationMenu && (
                                <motion.div
                                  initial={{ opacity: 0, y: 4 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  exit={{ opacity: 0, y: 4 }}
                                  className="absolute bottom-full right-0 mb-1 bg-surface-1 border border-border rounded-lg shadow-xl overflow-hidden min-w-[50px] z-10"
                                >
                                  {[1, 2, 3, 4].map((n) => (
                                    <button
                                      key={n}
                                      onClick={() => {
                                        setIterations(n)
                                        setShowIterationMenu(false)
                                      }}
                                      className={cn(
                                        'w-full px-3 py-2 text-xs text-left transition-colors',
                                        iterations === n
                                          ? 'bg-surface-2 text-foreground'
                                          : 'text-text-secondary hover:bg-surface-2 hover:text-foreground'
                                      )}
                                    >
                                      {n}x
                                    </button>
                                  ))}
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                          <button
                            onClick={onSubmit}
                            disabled={isRunning}
                            className={cn(
                              'h-8 px-4 flex items-center justify-center gap-2 rounded-full text-sm font-medium transition-all',
                              isRunning
                                ? 'bg-surface-2 text-text-muted cursor-not-allowed'
                                : 'bg-foreground hover:bg-foreground/90 text-background'
                            )}
                          >
                            {isRunning ? (
                              <>
                                <svg
                                  className="w-3.5 h-3.5 animate-spin"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                >
                                  <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                  />
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                  />
                                </svg>
                                <span>Generating...</span>
                              </>
                            ) : (
                              <>
                                <svg
                                  className="w-3.5 h-3.5"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                  strokeWidth={2.5}
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M4.5 10.5L12 3m0 0l7.5 7.5M12 3v18"
                                  />
                                </svg>
                                <span>Generate</span>
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Voice Tab */}
                  {activeTab === 'voice' && (
                    <div className="h-full p-4 overflow-y-auto">
                      <div className="flex flex-col items-center justify-center h-full text-center">
                        <div className="w-12 h-12 rounded-full bg-surface-2 flex items-center justify-center mb-3">
                          <svg
                            className="w-6 h-6 text-text-muted"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1.5}
                              d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
                            />
                          </svg>
                        </div>
                        <p className="text-sm text-foreground font-medium mb-1">
                          Voice Selection
                        </p>
                        <p className="text-xs text-text-muted mb-3">
                          Coming soon with Eleven Labs integration
                        </p>
                        {voiceName && (
                          <div className="flex items-center gap-2 px-3 py-1.5 bg-surface-2 rounded-lg">
                            <button
                              onClick={onPlayVoice}
                              disabled={!voiceSampleUrl}
                              className={cn(
                                'w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 transition-colors',
                                voiceSampleUrl
                                  ? 'bg-surface-3 hover:bg-border text-foreground'
                                  : 'bg-surface-1 text-text-muted cursor-not-allowed'
                              )}
                            >
                              {isVoicePlaying ? (
                                <svg
                                  className="w-3 h-3"
                                  fill="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                                </svg>
                              ) : (
                                <svg
                                  className="w-3 h-3 ml-0.5"
                                  fill="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path d="M8 5v14l11-7z" />
                                </svg>
                              )}
                            </button>
                            <span className="text-xs text-foreground">
                              {voiceName}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Variants Tab */}
                  {activeTab === 'variants' && (
                    <div className="h-full p-3 overflow-y-auto nowheel">
                      {variantCount === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full text-center">
                          <svg
                            className="w-8 h-8 text-text-muted/30 mb-2"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1}
                              d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                            />
                          </svg>
                          <p className="text-xs text-text-muted">
                            No variants yet
                          </p>
                          <p className="text-xxs text-text-muted/60 mt-1">
                            Generate to create variants
                          </p>
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {/* Pending spinners */}
                          {Array.from({ length: batchPending }).map(
                            (_, i) => (
                              <div
                                key={`pending-${i}`}
                                className="w-20 h-20 rounded-lg bg-surface-2 flex items-center justify-center border border-border-subtle"
                              >
                                <div className="w-4 h-4 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin" />
                              </div>
                            )
                          )}
                          {/* History items (newest first) */}
                          {[...history]
                            .reverse()
                            .map((entry, displayIdx) => {
                              const realIdx =
                                history.length - 1 - displayIdx
                              const isSelected = realIdx === pickedIndex
                              const imagePath = entry.result?.local_path
                              const thumbUrl =
                                imagePath && projectId
                                  ? getThumbnailUrl(
                                      projectId,
                                      imagePath,
                                      80,
                                      cacheKey
                                    )
                                  : null

                              return (
                                <button
                                  key={entry.result?.id || realIdx}
                                  onClick={() =>
                                    onSelectVariant?.(realIdx)
                                  }
                                  className={cn(
                                    'rounded-lg border-2 transition-all relative group',
                                    isSelected
                                      ? 'border-white p-0.5'
                                      : 'border-transparent hover:border-border'
                                  )}
                                >
                                  <div
                                    className={cn(
                                      'rounded-md overflow-hidden',
                                      isSelected ? 'rounded-[4px]' : ''
                                    )}
                                  >
                                    {thumbUrl ? (
                                      <img
                                        src={thumbUrl}
                                        alt={`v${realIdx + 1}`}
                                        className="h-20 w-auto block rounded-md"
                                      />
                                    ) : (
                                      <div className="w-20 h-20 bg-surface-2 flex items-center justify-center">
                                        <svg
                                          className="w-4 h-4 text-text-muted/30"
                                          fill="none"
                                          viewBox="0 0 24 24"
                                          stroke="currentColor"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={1}
                                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16"
                                          />
                                        </svg>
                                      </div>
                                    )}
                                  </div>
                                  {isSelected && (
                                    <div className="absolute top-1.5 right-1.5">
                                      <span className="text-white text-sm">
                                        &#9733;
                                      </span>
                                    </div>
                                  )}
                                  <div className="absolute bottom-1.5 left-1.5 text-xxs text-white bg-black/60 px-1 rounded">
                                    v{realIdx + 1}
                                  </div>
                                </button>
                              )
                            })}
                        </div>
                      )}
                    </div>
                  )}

                  {/* References Tab */}
                  {activeTab === 'references' && (
                    <div className="h-full p-3 overflow-y-auto nowheel">
                      <div className="flex flex-wrap gap-2">
                        {referenceThumbnails.map((ref) => (
                          <div
                            key={ref.index}
                            className="relative w-14 h-14 rounded-lg overflow-hidden border border-border group"
                          >
                            <img
                              src={ref.url}
                              alt={`Ref ${ref.index + 1}`}
                              className="w-full h-full object-cover"
                            />
                            {onRemoveImage && (
                              <button
                                onClick={() => onRemoveImage(ref.index)}
                                className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-black/70 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <svg
                                  className="w-2.5 h-2.5"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                  strokeWidth={2}
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M6 18L18 6M6 6l12 12"
                                  />
                                </svg>
                              </button>
                            )}
                          </div>
                        ))}
                        {/* Add button */}
                        <button
                          onClick={() => setShowImagePicker(true)}
                          disabled={!projectId}
                          className="w-14 h-14 rounded-lg border-2 border-dashed border-border hover:border-border-subtle bg-surface-1 hover:bg-surface-2 flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <svg
                            className="w-5 h-5 text-text-muted"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={1.5}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M12 4v16m8-8H4"
                            />
                          </svg>
                        </button>
                      </div>
                      {referenceImages.length === 0 && (
                        <p className="text-xs text-text-muted mt-3 text-center">
                          Add reference images to guide character
                          generation
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </>
    )
  }
)

CharacterNodeBottomPanel.displayName = 'CharacterNodeBottomPanel'
